import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IResolvedLocalMap, IShow, Roles} from "@evennt-tech/api-v2-core";
import {apiV2} from "api/firebase";
import {
  definitionGridToSchema,
  drawerGridToFormAnswer,
  overlayRequiredFromDefinition,
  snapshotGridToForm,
} from "tools/task/grid/mappers";
import {useCurrentShowId} from "hooks";
import {selectUser} from "store/auth/selectors";
import BrandedShowLayout from "view/components/BrandedShowLayout";
import GridResponse from "view/components/TasksList/components/ResponseBlock/components/Grid";
import type {
  GridForm as GridFormType,
  Grid as GridResponseType,
} from "ts/api/task";
import type {GridTaskLike} from "ts/api/grid-task-like";
import FfairPaper from "view/components/FfairPaper";
import {TaskFileShareProvider} from "view/context/TaskFileShareContext";
import FlexBox from "view/components/Layout/FlexBox";
import {Button, CircularProgress, Typography} from "@material-ui/core";
import DataRow from "view/components/DataRow";
import {format} from "date-fns";
import {getTaskDueDay} from "tools/task/dueDate";
import {getReadableString} from "constraints/general";
import BusinessIcon from "@material-ui/icons/Business";
import PinDropIcon from "@material-ui/icons/PinDrop";
import BuildIcon from "@material-ui/icons/Build";
import EventNoteIcon from "@material-ui/icons/EventNote";
import {grey} from "@material-ui/core/colors";
import {useTranslate} from "@tolgee/react";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import useStyles from "./styles";

type Data = {
  task: GridTaskLike & {title?: string; additionalRowNames?: string[]};
  // The positional form-grid the renderer seeds into `useForm<GridFormType>`
  // (what `drawerGridToFormAnswer` returns / the postMessage path posts).
  response: GridFormType["grid"];
  standNumber?: string;
  buildType?: string;
  // Arrives as a Date-parseable string or undefined; the render path must
  // tolerate the missing case.
  dueDate?: Date | string | null;
  companyName?: string;
};

// Rendered UNDER the branding `ThemeProvider` so its own `useStyles()` (which
// reads `palette.otherElementsBackground` / `contrastText` / `separator` /
// `tableDataRow`) resolves against the show theme. Keeping it a child of the
// provider is what makes the whole page — not just the inner `<Grid>` — honour
// a DARK show. If `useStyles` were called in the parent it would bind to the
// app-root light theme sitting above the provider.
const GridPreviewContent = ({
  data,
  dateFormat,
  localMap,
}: {
  data: Data;
  dateFormat: string;
  localMap: IResolvedLocalMap;
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  // `window.close()` only works for a window our app script-opened (the in-app
  // "Open Grid" via `window.open`, which sets `window.opener`). A window opened
  // straight from a PDF link has no opener, so `close()` is a no-op — hide the
  // button there instead of showing one that does nothing.
  const canClose = typeof window !== "undefined" && !!window.opener;

  return (
    <FfairPaper className={classes.root}>
      <FlexBox column gap={3}>
        <Typography className={classes.title}>{data.task.title}</Typography>
        <FlexBox column width="100%">
          <DataRow
            label={localMap.exhibitor}
            icon={<BusinessIcon htmlColor={grey[500]} />}
            value={data?.companyName}
          />
          <DataRow
            label={t("common.tasks.settings.stand_number", {
              stand: localMap.stand,
            })}
            icon={<PinDropIcon htmlColor={grey[500]} />}
            value={data?.standNumber}
          />
          <DataRow
            label={t("exhibitor.dashboard.info.build_type")}
            icon={<BuildIcon htmlColor={grey[500]} />}
            value={data?.buildType}
          />
          <DataRow
            label={t("common.tasks.settings.due_date")}
            icon={<EventNoteIcon htmlColor={grey[500]} />}
            value={(() => {
              if (!data?.dueDate) return "—";
              const d = new Date(data.dueDate);
              // Due date is a calendar DAY stored as a UTC instant — collapse
              // to the UTC day before formatting so the rendered day doesn't
              // drift with the viewer's timezone (see tools/task/dueDate.ts).
              const day = Number.isFinite(d.getTime())
                ? getTaskDueDay(d)
                : null;
              return day ? format(day, dateFormat, {locale}) : "—";
            })()}
          />
        </FlexBox>
        <FlexBox column>
          <Typography className={classes.responseGrid}>
            {t("common.tasks.response.type_title", {
              type: t("common.tasks.grid.title"),
            })}
          </Typography>
          <Typography className={classes.tableName}>
            {t("common.tasks.grid.table_name", {
              name: data.task.gridSchema?.name,
            })}
          </Typography>
          {/* Reuse the drawer's grid renderer: it seeds the form via
              `useForm({ defaultValues })` so every cell (text / options / files)
              renders read-only. Hand-seeding a form with `setValue` after mount
              (the previous approach) left text/options cells blank because their
              inputs read via non-subscribing `form.watch`. */}
          <div className={classes.grid} style={{background: "inherit"}}>
            <GridResponse
              response={data.response as GridResponseType}
              task={data.task}
            />
          </div>
          {canClose && (
            <FlexBox alignCenter justifyContent="flex-end">
              <Button color="primary" onClick={() => window.close()}>
                {t("common.tasks.history.close_caps")}
              </Button>
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    </FfairPaper>
  );
};

// Centered loading / not-found screen for the chromeless preview window. Calls
// `useStyles` itself so it works as a plain page (no show branding needed for a
// spinner / message).
const StatusScreen = ({state}: {state: "loading" | "notFound"}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  return (
    <FlexBox
      className={classes.statusRoot}
      column
      alignCenter
      justifyCenter
      gap={2}
    >
      {state === "loading" ? (
        <CircularProgress />
      ) : (
        <>
          <Typography className={classes.statusTitle}>
            {t("common.tasks.grid.preview.not_found_title")}
          </Typography>
          <Typography className={classes.statusSubtitle}>
            {t("common.tasks.grid.preview.not_found_message")}
          </Typography>
        </>
      )}
    </FlexBox>
  );
};

const GridPreview = () => {
  const [data, setData] = useState<Data | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "notFound">(
    // No `responseId` in the URL → nothing to load.
    new URLSearchParams(window.location.search).get("responseId")
      ? "loading"
      : "notFound"
  );
  const [dateFormat, setDateFormat] = useState<string>(getReadableString());
  // Show-configured local-map terms (e.g. "Exhibitor" → "Vendor"). Only ever
  // populated for a logged-in exhibitor — see the gated effect below.
  const [localMapShow, setLocalMapShow] = useState<Pick<
    IShow,
    "localMap"
  > | null>(null);
  // Single mechanism: the page always loads one response version by id from the
  // session-aware `task/grid-preview/:responseId` endpoint. Same URL for the
  // in-app "Open Grid" buttons and the anonymous link baked into PDFs — a
  // logged-in viewer with show access sees any version, anonymous sees only
  // published ones. No session-gated hooks here, so a logged-out reader never
  // triggers a 401 / forced logout.
  const responseId = new URLSearchParams(window.location.search).get(
    "responseId"
  );
  const showId = useCurrentShowId();
  const {role} = useSelector(selectUser);
  const isExhibitor = role === Roles.EXHIBITOR;
  // This page is the ONLY place the console renders a GRID answer's file cells
  // for a sub-task (the drawer shows `CutGrid` — a name + "Open Grid" button),
  // so the per-file "copy link" affordance has to live here too.
  //
  // Gated on the roles `task/file/:fileId/share` accepts (organiser /
  // exhibitor of the show): the same URL is the anonymous link baked into
  // certificate / task-print PDFs, and that reader has no session to publish
  // with — nothing marks a grid cell's files public for them, so they get no
  // copy button rather than one that 403s.
  const canPublishFiles = role === Roles.ORGANISER || role === Roles.EXHIBITOR;

  useEffect(() => {
    if (!responseId) {
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    apiV2.task
      .getPublicGridPreview({responseId})
      .then((res) => {
        if (cancelled) return;
        // Prefer the response's immutable per-cell SNAPSHOT (self-contained:
        // every cell placed by its own row/column position) — the same path the
        // drawer / ResponseBlock use. Definition-based mapping is only a
        // fallback for a version with no snapshot; on its own it misaligned
        // cells here (values landed only in the first column, others blank).
        // Graft the table name + includeRowTitles from the live definition
        // (the snapshot hardcodes name "" and omits definition-only props).
        const definitionSchema = definitionGridToSchema(res.task.gridSchema);
        const fromSnapshot = snapshotGridToForm(res.response);
        const reconstructed = fromSnapshot.gridSchema
          ? {
              gridSchema: {
                ...fromSnapshot.gridSchema,
                name: definitionSchema?.name ?? fromSnapshot.gridSchema.name,
                includeRowTitles:
                  definitionSchema?.includeRowTitles ??
                  fromSnapshot.gridSchema.includeRowTitles,
              },
              grid: fromSnapshot.grid,
              additionalRowNames: [] as string[],
            }
          : definitionSchema
          ? drawerGridToFormAnswer(definitionSchema, res.response)
          : {gridSchema: null, grid: [], additionalRowNames: []};
        // The snapshot carries no `required` (definition property) — without
        // this graft every cell renders as optional and the mandatory-cell
        // icon disappears, diverging from the drawer's read-only grid.
        overlayRequiredFromDefinition(
          reconstructed.gridSchema,
          definitionSchema
        );
        setDateFormat(getReadableString({dateFormat: res.meta.dateFormat}));
        setData({
          task: {
            id: res.task.id,
            title: res.task.title,
            gridSchema: reconstructed.gridSchema,
            additionalRowNames: reconstructed.additionalRowNames,
          },
          response: reconstructed.grid,
          standNumber: res.meta.standNumber,
          buildType: res.meta.buildType,
          dueDate: res.meta.dueDate,
          companyName: res.meta.companyName,
        });
        setStatus("ready");
      })
      // 404 (unknown / unauthorised responseId) or any load failure → tell the
      // viewer nothing was found rather than hanging on a blank page.
      .catch(() => {
        if (!cancelled) setStatus("notFound");
      });
    return () => {
      cancelled = true;
    };
  }, [responseId]);

  // The custom local map is an exhibitor-facing concern and the show endpoint
  // needs a session: fetch it ONLY for a logged-in exhibitor. Organisers and
  // anonymous PDF readers keep the platform default terms and — as documented
  // above — still fire no session-gated request from this public route.
  useEffect(() => {
    if (!isExhibitor || !showId) {
      setLocalMapShow(null);
      return undefined;
    }
    let cancelled = false;
    apiV2.show
      .getOne({showId})
      .then((show) => {
        if (!cancelled) setLocalMapShow(show ?? null);
      })
      // Terms are cosmetic — on failure fall back to the defaults silently.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isExhibitor, showId]);

  const localMap = useLocalizedLocalMap(localMapShow);

  if (status !== "ready" || !data) {
    return (
      <StatusScreen state={status === "loading" ? "loading" : "notFound"} />
    );
  }

  // `showId` undefined → the provider publishes no context, and every chip
  // below renders without the copy icon.
  const content = (
    <TaskFileShareProvider
      showId={canPublishFiles ? showId : undefined}
      mode="public"
    >
      <GridPreviewContent
        data={data}
        dateFormat={dateFormat}
        localMap={localMap}
      />
    </TaskFileShareProvider>
  );

  // Branding: a logged-in exhibitor keeps the show theme; organisers and
  // anonymous viewers get the neutral default.
  return isExhibitor && showId ? (
    <BrandedShowLayout showId={showId}>{content}</BrandedShowLayout>
  ) : (
    content
  );
};

export default GridPreview;
